/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { inspect } from 'util';
import Cookies from 'js-cookie';
import { Model } from '@adobe/aem-spa-page-model-manager';
import forge from 'node-forge';
import { DCA_CONSTANTS, HYBRID_CONFIG_CONSTANTS, SignInconstants, ENCRYPT_KEY_ENDPOINT } from './constants';
import { mockSessionData } from './_localDevMocks/session-mock';
import { logger } from '@marriott/mi-headless-utils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import DOMPurify from 'isomorphic-dompurify';
import { AnyARecord } from 'dns';

const {
  NEXT_PUBLIC_DYNAMIC_ROUTING_URL,
  ROUTE_REQUEST_THROUGH_HYBRID_APP,
  SESSION_APP_CALL_URL,
  NEXT_PUBLIC_FEATURE_FLAG_URL,
  NEXT_PUBLIC_AEM_PUBLISH,
  NEXT_AEM_AUTH,
  NEXT_PUBLIC_DYNAMIC_ROUTING_AUTHOR_URL,
  ENABLE_DYNAMIC_LOCALE_REQUEST,
} = process.env;
export const canUseDOM = !!(typeof window !== 'undefined' && window.document);

export function parseValues(inputString: string) {
  if (!inputString) {
    return { marshaCode: null, confirmationNumber: null };
  }

  const pairs = inputString.split('&');
  const values: any = {};

  pairs.forEach(pair => {
    const [key, value] = pair.split('=');
    values[key] = decodeURIComponent(value);
  });

  const { marshaCode, confirmationNumber } = values;

  if (!marshaCode && !confirmationNumber) {
    return { marshaCode: null, confirmationNumber: null };
  }

  return { marshaCode, confirmationNumber, error: null };
}

// Captures every query-string key/value pair from a URL query segment so they can be
// forwarded to Target personalization as `parameters`. Returns undefined when there is
// nothing to send, so the personalization request omits the parameters object entirely.
export function parseUrlParameters(queryString: string): Record<string, string> | undefined {
  if (!queryString) {
    return undefined;
  }

  const params: Record<string, string> = {};

  queryString.split('&').forEach(pair => {
    if (!pair) {
      return;
    }

    const separatorIndex = pair.indexOf('=');
    const rawKey = separatorIndex === -1 ? pair : pair.slice(0, separatorIndex);
    const rawValue = separatorIndex === -1 ? '' : pair.slice(separatorIndex + 1);

    let key = rawKey;
    let value = rawValue;

    try {
      key = decodeURIComponent(rawKey);
    } catch {
      key = rawKey;
    }

    try {
      value = decodeURIComponent(rawValue);
    } catch {
      value = rawValue;
    }

    if (!key) {
      return;
    }

    params[key] = value;
  });

  return Object.keys(params).length > 0 ? params : undefined;
}

export const getProcessEnvs = () => {
  const processENV = process.env && { ...process.env };
  delete processENV['NEXT_AEM_AUTH'];
  delete processENV['NEXT_PUBLIC_AEM_AUTH'];
  if (canUseDOM) {
    const envTag = document.getElementById('__SERVERENV__');
    if (envTag) {
      const envObject = JSON.parse(envTag.innerHTML);
      return envObject;
    }
    return processENV;
  }
  return processENV;
};

export async function getCachedPageModel(
  path: string,
  locale: string,
  sessionID: string,
  applicationName: string,
  selector: string,
  requestId = '',
  isAuthenticated = false,
  eaaPageIndicator = '',
  PREPROCESSOR_EXPERIENCE_SEGMENT: Array<string>
) {
  const { log, pLog } = global.loggerInstance('ModelCall');
  const { ROUTING_CONFIG, EXPERIENCE_SEGMENT, EXPERIENCE_SEGMENT_AUTHENTICATED } = HYBRID_CONFIG_CONSTANTS;
  locale = locale?.replace('_', '-');
  const pagePath = path.split('?')[0];

  const getCMSTemplateReqBody: {
    requestType: string;
    seoUrl: string;
    localeKey: string;
    experienceSegment: Array<string>;
    applicationName: string;
    sessionToken: string;
    selector?: string;
    eaaPageIndicator?: string;
    selectors?: string;
  } = {
    requestType: ROUTING_CONFIG,
    seoUrl: pagePath,
    localeKey: locale,
    experienceSegment: PREPROCESSOR_EXPERIENCE_SEGMENT
      ? PREPROCESSOR_EXPERIENCE_SEGMENT
      : isAuthenticated
      ? [...EXPERIENCE_SEGMENT_AUTHENTICATED]
      : [...EXPERIENCE_SEGMENT],
    applicationName: applicationName,
    sessionToken: sessionID,
  };

  if (selector) {
    getCMSTemplateReqBody['selectors'] = selector;
  }
  if (eaaPageIndicator) {
    getCMSTemplateReqBody['experienceSegment'] = [...getCMSTemplateReqBody['experienceSegment'], eaaPageIndicator];
  }

  let pageModel = {};
  const apiStartTime = new Date().getTime();
  try {
    log.error(`NEXT_PUBLIC_DYNAMIC_ROUTING_URL: ${NEXT_PUBLIC_DYNAMIC_ROUTING_URL}`);
    log.error(`getCMSTemplateReqBody: ${JSON.stringify(getCMSTemplateReqBody)}`);
    const response = await axios.post(`${NEXT_PUBLIC_DYNAMIC_ROUTING_URL}`, getCMSTemplateReqBody, {
      headers: {
        'x-request-id': requestId,
      },
    });
    log.error(`AEM model API call success with status code: ${response.status}`);
    if (response) {
      log.error(`AEM model recieved: ${inspect(response)}`);
      pageModel = response.data.data;
    } else {
      log.error(`AEM model failure: ${response}`);
      pageModel = {};
    }
  } catch (err) {
    log.error(`API call failed: ${err}`);
    //returing page model and logging error
    return pageModel;
  }
  pLog.log('API call performance timing', apiStartTime, new Date().getTime());
  return pageModel;
}

export async function getPageModel(
  path: string,
  locale: string,
  selector: string,
  noCache: string,
  isAppbuilderDemo: boolean,
  requestId = ''
) {
  const { log, pLog } = global.loggerInstance('ModelCall');
  locale = locale?.replace('_', '-');
  const pagePath = path.split('?')[0];

  let pageModel = {};
  const apiStartTime = new Date().getTime();
  try {
    log.error(`Calling NEXT_PUBLIC_DYNAMIC_ROUTING_AUTHOR_URL: ${NEXT_PUBLIC_DYNAMIC_ROUTING_AUTHOR_URL}`);
    const response = await axios.get(`${NEXT_PUBLIC_DYNAMIC_ROUTING_AUTHOR_URL}`, {
      headers: {
        Authorization: NEXT_AEM_AUTH as string,
        'x-request-id': requestId,
      },
    });
    log.error(`AEM routes API call success with status code: ${response.status}`);
    if (response) {
      log.error(`AEM SEO page route: ${inspect(pagePath)}`);
      log.error(`AEM path: ${inspect(path)}`);
      //log.error(`AEM page route debug: ${inspect(response.data)}`);
      let resolvedTemplate: string | null = null;
      const aemPageRoute = response.data.routingListMultifield.find(
        (route: { seourl: string; aempagetemplate: string }) => {
          if (route.seourl === pagePath) {
            resolvedTemplate = route.aempagetemplate;
            return true;
          }
          try {
            const match = pagePath.match(new RegExp(`^${route.seourl}$`));
            if (match) {
              const captured = (match[1] || '').replace(/\.mi$/, '');
              resolvedTemplate = route.aempagetemplate.replace('$1', captured);
              return true;
            }
          } catch {
            log.error(`Error matching AEM route regex`);
          }
          return false;
        }
      );
      log.error(`AEM page route: ${inspect(aemPageRoute.aempagetemplate)}`);
      log.error(`AEM selector: ${inspect(selector)}`);
      if (selector != null) {
        selector = '.model.' + selector;
      } else {
        selector = '.model';
      }
      let aemReqUrl;
      log.error(`noCache val: ${noCache}`);
      log.error(`Page call 2 : confirmationNumber : ${isAppbuilderDemo}`);
      if (noCache === 'false') {
        aemReqUrl = resolvedTemplate + selector + '.json';
      } else {
        if (isAppbuilderDemo) {
          aemReqUrl = resolvedTemplate + selector + '.appBuilderDemo.json?nocache=' + noCache;
        } else {
          aemReqUrl = resolvedTemplate + selector + '.json?nocache=' + noCache;
        }
      }
      log.error(`AEM model URL: ${aemReqUrl}`);
      const modelResponse = await axios.get(aemReqUrl, {
        headers: {
          marshaCode: selector,
          Authorization: NEXT_AEM_AUTH as string,
          'x-request-id': requestId,
        },
      });
      if (modelResponse) {
        log.error(`AEM model recieved: ${inspect(modelResponse)}`);
        pageModel = modelResponse.data;
      } else {
        log.error(`AEM model failure: ${modelResponse}`);
        pageModel = {};
      }
    } else {
      log.error(`AEM routes failure: ${response}`);
      pageModel = {};
    }
  } catch (err) {
    log.error(`API call failed: ${err}`);
    //returing page model and logging error
    return pageModel;
  }
  pLog.log('API call performance timing', apiStartTime, new Date().getTime());
  return pageModel;
}

//converts ":items" format to "cqItems" format.
export function transformToCQ(propKey: string) {
  const tempKey = propKey.substring(1);

  return 'cq' + tempKey.substring(0, 1).toUpperCase() + tempKey.substring(1);
}

//To format mock model in local dev, in other envs this formatting is taken care by OOTB fetchModel utility
export const respGridUtil = (item: Model) => {
  if (!item || !Object.keys(item).length) {
    return { cqPath: '' };
  }

  const keys = Object.getOwnPropertyNames(item);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const props: any = {};

  keys.forEach((key: string) => {
    const propKey = key.startsWith(':') ? transformToCQ(key) : key;
    props[propKey] = item[key as keyof Model] || '';
  });

  return props;
};

export async function getSessionData(sessionIDVal: string, isLocalDev: boolean, isAuthorMode: boolean, requestId = '') {
  return new Promise((resolve, reject) => {
    let sessionData;
    let sessionHeader: any = {};
    const { log } = global.loggerInstance('SessionCall');
    const sessionGetCallUrl =
      SESSION_APP_CALL_URL || useClientEnvVarsStore.getState().envVarsObject['SESSION_GET_CALL_URL_CLIENT'];

    if (isLocalDev || isAuthorMode) {
      log.error(`[Session Data] Detected local or Author mode, using mock session`);
      resolve({ sessionData: mockSessionData, sessionHeader: '' });
    } else {
      const apiStartTime = new Date().getTime();
      log.error(`[Session Data] SESSION_APP_CALL_URL: ${sessionGetCallUrl}`);
      log.error(`[Session Data] session token: ${sessionIDVal}`);
      axios
        .get(`${sessionGetCallUrl}`, {
          headers: {
            Cookie: `sessionID=${sessionIDVal}`,
            'Cache-Control': 'no-store, no-cache, private',
            'x-request-id': requestId,
          },
        })
        .then(resp => {
          sessionHeader = resp?.headers;
          sessionData = resp?.data;
          log.error('[Session Data] Session Data Loaded', sessionData);
          resolve({ sessionData, sessionHeader });
        })
        .catch(e => {
          log.error('[Session Data] Error with session route!', e);
          reject('session');
        });
      log.error('API call performance timing', apiStartTime, new Date().getTime());
    }
  });
}

export async function getSessionDataWithParams(
  preHeader: Record<string, string>,
  isLocalDev: boolean,
  isAuthorMode: boolean
) {
  return new Promise((resolve, reject) => {
    let sessionData;
    let sessionHeader: any = {};
    const { log } = global.loggerInstance('SessionCall');

    const sessionBody = { keys: process.env['NEXT_PUBLIC_SESSION_KEYS'] };

    if (isLocalDev || isAuthorMode) {
      log.error(`[Session Data] Detected local or Author mode, using mock session`);
      resolve({ sessionData: mockSessionData, sessionHeader: '' });
    } else {
      const apiStartTime = new Date().getTime();
      log.error(`[Session Data] SESSION_APP_CALL_URL: ${SESSION_APP_CALL_URL}`);
      log.error(`[Session Data] session token: ${preHeader}`);
      axios
        .post(`${SESSION_APP_CALL_URL}`, sessionBody, {
          headers: preHeader,
        })
        .then(resp => {
          sessionHeader = resp?.headers;
          sessionData = resp?.data;
          log.error('[Session Data] Session Data Loaded', sessionData);
          resolve({ sessionData, sessionHeader });
        })
        .catch(e => {
          log.error('[Session Data] Error with session route!', e);
          reject('session');
        });
      log.error('API call performance timing', apiStartTime, new Date().getTime());
    }
  });
}

export async function getV1SessionData(
  sessionIDVal: string,
  isLocalDev: boolean,
  isAuthorMode: boolean,
  searchKeys: string,
  requestId = ''
) {
  return new Promise((resolve, reject) => {
    let sessionData;
    let sessionHeader: any = {};
    const { log } = logger({ requestID: '', sessionID: '' })('SessionCall');

    const sessionApiUrl =
      process.env['SESSION_APP_GET_CALL_V1_URL'] ||
      useClientEnvVarsStore.getState().envVarsObject['SESSION_APP_GET_CALL_V1_URL'];
    if (isLocalDev || isAuthorMode) {
      log.error(`[Session Data] Detected local or Author mode, using mock session`);
      resolve({ sessionData: mockSessionData, sessionHeader: '' });
    } else {
      const apiStartTime = new Date().getTime();
      log.error(`[Session Data] sessionApiUrl: ${sessionApiUrl}`);
      log.error(`[Session Data] session token: ${sessionIDVal}`);
      axios
        .post(
          `${sessionApiUrl}`,
          {
            keys: searchKeys,
          },
          {
            headers: {
              Cookie: `sessionID=${sessionIDVal}`,
              'x-request-id': requestId,
            },
          }
        )
        .then(resp => {
          sessionHeader = resp?.headers;
          sessionData = resp?.data;
          log.error('[Session Data] Session Data Loaded', sessionData);
          resolve({ sessionData, sessionHeader });
        })
        .catch(e => {
          log.error('[Session Data] Error with session route!', e);
          reject('session');
        });
      log.error('API call performance timing', apiStartTime, new Date().getTime());
    }
  });
}

export function getDataLayerScript(dataLayer: { data: any; mvpOffersData: string }) {
  return `var dataLayer = ${dataLayer?.data ? JSON.stringify(dataLayer?.data[0]) : '{}'}; var mvpOffers = ${
    dataLayer?.mvpOffersData && dataLayer?.mvpOffersData !== 'null' ? dataLayer?.mvpOffersData : '{}'
  };`;
}

export function getSessionDataScript(sessionData: Record<string, string>) {
  return `var sessionData = ${sessionData ? JSON.stringify(sessionData) : '{}'};`;
}

export async function getFeatureFlagData() {
  const { log } = global.loggerInstance('MandatoryFee');
  log.error(`[Feature Flag] API url: ${NEXT_PUBLIC_FEATURE_FLAG_URL}`);
  return new Promise((resolve, reject) => {
    axios
      .get(`${NEXT_PUBLIC_FEATURE_FLAG_URL}`)
      .then(res => {
        resolve(res.data);
      })
      .catch(e => {
        log.error('[Feature Flag] Error with feature flag route!', e);
        reject('featureflag');
      });
  });
}

export const mtGC = (cname: string): string => {
  const name = cname + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      const cnameValue = c.substring(name.length, c.length);
      try {
        return decodeURIComponent(cnameValue);
      } catch {
        return cnameValue;
      }
    }
  }
  return '';
};

export const mtGCV = (cname: string): string => {
  const x: Array<string> = mtGC(cname).split('|');
  let i;
  for (i = 0; i < x.length; i++) {
    if (x[i] === DCA_CONSTANTS.MCMID_COOKIE) {
      return x[i + 1];
    }
  }
  return '';
};

export const handleRedirect = (url = '', isOverlay = false, decode = true) => {
  /** handle redirect in this file according to the error code */
  const location = isOverlay ? window.parent.location : window.location;

  // Ensure the URL is properly sanitized and validated
  const sanitizedUrl = decode ? validateUrl(url, location.origin) : new URL(url, location.origin).href;

  if (sanitizedUrl) {
    location.href = sanitizedUrl;
  }
};

const validateUrl = (url = '', origin = ' ') => {
  // Use a regular expression to check if the URL starts with a "/"
  const decodedUrl = decodeURIComponent(url);
  // const isRelativePath = /^\/[^/]/.test(decodedUrl); TODO

  try {
    // Use the URL constructor to validate and sanitize the URL
    // const validatedUrl = new URL(isRelativePath ? decodedUrl : `https://${decodedUrl}`, origin); TODO
    const validatedUrl = new URL(decodedUrl, origin);

    return validatedUrl.href;
  } catch (error) {
    // Handle invalid URL or other errors
    return null;
  }
};

export function convertToPrice(number: number, currency: any, locale = 'en-US'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(number);
}

export const combinedObject = (obj1: any, obj2: any): any => {
  function mergeRecursive(target: any, source: any) {
    for (const key in source) {
      if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
        continue;
      }

      if (!Object.prototype.hasOwnProperty.call(source, key)) {
        continue;
      }

      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        if (!target[key]) {
          Object.assign(target, { [key]: {} });
        }
        mergeRecursive(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  }

  const mergedObject = { ...obj1 };
  mergeRecursive(mergedObject, obj2);
  return mergedObject;
};

export const capitalize = (name: string) => {
  return name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : '';
};
export interface PageModelType {
  [key: string]: any;
  ':items'?: { [key: string]: any };
}

export const getItemFromPageModel = (pageModel: PageModelType, itemPath: string) => {
  const parts = itemPath?.split('/');
  let obj = pageModel;

  for (let i = 0; i < parts?.length && obj; i++) {
    obj = (obj[':items'] || {})[parts[i]];
  }

  return obj || {};
};

export const formatLanguageDataForSelector = (data: any, showMock: boolean) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let result: any = {};

  if (showMock) {
    result = data;
  } else {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data.forEach((item: any, index: any) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const columnKey: any = `column${index + 1}`;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result[columnKey] = {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [item.region]: item.languages.map((lang: any) => ({
          linkLabel: lang.linkTitle,
          openinnewwtab: lang.openInNewTab !== null ? lang.openInNewTab : false,
          linkURL: lang.linkPath,
        })),
      };
    });
  }

  return result;
};
/**
 * getCurrentUrlParams() should return search parameters by reading current url from window.location
 */
export const getCurrentUrlParams = (isOverlay?: boolean) => {
  return isOverlay ? window?.parent?.location?.search : window?.location?.search;
};
// convert locale string to {countryCode: '', langCode: ''}
// This will take the current locale string as parameter and return a object with [countryCode] and [langCode] as a key
export const getCountryAndLangCode = (input: string) => {
  let currentLocaleArr;
  if (input?.includes('_')) currentLocaleArr = input?.split('_');
  if (input?.includes('-')) currentLocaleArr = input?.split('-');
  return {
    countryCode: currentLocaleArr && currentLocaleArr[1],
    langCode: currentLocaleArr && currentLocaleArr[0],
  };
};
// check if locale is set to china
// This will take the current locale string as parameter and return a boolean based on country code(China=CN) condition check
export const checkChinaLocale = (currLocale: string) => {
  const { countryCode } = getCountryAndLangCode(currLocale);
  return countryCode?.toUpperCase() === SignInconstants.CHINA_COUNTRY_CODE;
};

export const checkUSLocale = (currLocale: string) => {
  const { countryCode } = getCountryAndLangCode(currLocale);
  return countryCode?.toUpperCase() === SignInconstants.USA_COUNTRY_CODE;
};

export const checkKOLocale = (currLocale: string) => {
  const { countryCode } = getCountryAndLangCode(currLocale);
  return countryCode?.toUpperCase() === SignInconstants.KOREA_COUNTRY_CODE;
};

export const checkAELocale = (currLocale: string) => {
  const { countryCode } = getCountryAndLangCode(currLocale);
  return countryCode?.toUpperCase() === SignInconstants.ARABIC_COUNTRY_CODE;
};

export const encryptPassword = (password: string, publicKey: string, enableEncryption: string) => {
  const isEnableEncryption = enableEncryption && JSON.parse(enableEncryption);
  if (isEnableEncryption && publicKey && publicKey !== null) {
    const key = forge.pki.publicKeyFromPem(publicKey);

    const encryptedPass = key.encrypt(password, 'RSA-OAEP', {
      md: forge.md.sha256.create(),
      mgf1: {
        md: forge.md.sha256.create(),
      },
    });

    return forge.util.encode64(encryptedPass);
  } else {
    return password;
  }
};
const findNthOccurence = function (str: string, nth: number, char: string) {
  let index = 0;
  for (let i = 0; i < nth; i += 1) {
    if (index !== -1) index = str.indexOf(char, index + 1);
  }
  return index;
};
/**
 *
 * @param prefix
 * This is used for setting the prefix value to the variable from Page level attribute in model.json.
 */
let SUBDIRECTORY_PREFIX = '';
export function setSubDirectoryPrefix(prefix = '') {
  SUBDIRECTORY_PREFIX = prefix;
}
export const addSubDirectoryPrefix = function (url = '') {
  SUBDIRECTORY_PREFIX =
    SUBDIRECTORY_PREFIX || (canUseDOM ? window?.sessionStorage?.getItem('SUBDIRECTORYCONTEXTPATH') : '') || '';
  if (
    SUBDIRECTORY_PREFIX &&
    SUBDIRECTORY_PREFIX.toLowerCase() !== 'en-us' &&
    url?.startsWith('/') &&
    url?.substring(1, findNthOccurence(url, 1, '/'))?.toLowerCase() !== SUBDIRECTORY_PREFIX?.toLowerCase()
  ) {
    return SUBDIRECTORY_PREFIX ? '/' + SUBDIRECTORY_PREFIX + url : url;
  } else return url;
};

export const replaceText = (template: string, replacements: string[]) => {
  if (template) {
    return template.replace(/\{(\d+)\}/g, (match, index) => replacements[index] || match);
  }
  return '';
};

export const skipDecode = (redirectUrl: string | null, skipPaths: string | undefined) => {
  const paths = skipPaths?.split(',') ?? [];
  let decode = false;
  if (paths.length > 0) {
    decode = paths?.some(path => redirectUrl?.includes(path));
  }
  return decode;
};
export enum DATALAYER_PAGE_EVENT {
  EARNING_PREFERENCE_SUCCESS = 'earning-preference-success',
  CONVERT_POINTS_SUCCESS = 'convert-points-success',
  CONVERT_POINTS_FAILURE = 'convert-points-failure',
  LOGIN_SUCCESS = 'login-success',
  LOGIN_FAILURE = 'login-failure',
  PROMOTION = 'promotion',
}
let ACCOUNT_DATALAYER_COUNTER: number = 0;
const updateDataLayerCounter = () => {
  ACCOUNT_DATALAYER_COUNTER = ACCOUNT_DATALAYER_COUNTER + 1;
};

export const updateDataLayerProperties = (dataLayerProperties: { [key: string]: unknown }, pageEvent: string) => {
  if (canUseDOM) {
    const dataLayer = window.dataLayer ?? {};
    Object.keys(dataLayerProperties).forEach(key => {
      dataLayer[key] = dataLayerProperties[key];
    });
    // update counter before updating hash
    updateDataLayerCounter();
    // Update the window.location.hash with the ACCOUNT_DATALAYER_COUNTER
    window.history.replaceState({}, '', `#page_name=${pageEvent}-${ACCOUNT_DATALAYER_COUNTER}`);
  }
};

export const signMobileRegex = /^[0-9+ ]+$/;
//sign in email or member id should not have consecutive asterix
export const signEmailMemberIdAsterixRegex = /[*]{3,}/;

//sign in email validation, matches digits, alphabets and some special chars
export const signEmailRegex = /^[_0-9A-Za-z-.@ ]+$/;
export const CANADA_ZIP_CODE_REGEX = new RegExp(/^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$/);
export const US_ZIP_CODE_REGEXT = new RegExp(/^\d{5}[-]?(?:\d{4})?$/);
// validates email addresses by ensuring they contain alphanumeric characters, dots, hyphens, underscores, and an "@" symbol, followed by a valid domain.
export const emailFormatRegex = new RegExp('^[\\w-_\\.+]*[\\w-_\\.]\\@([\\w]+\\.)+[\\w]+[\\w]$');
//it only accepts numbers
export const numberRegex = /^\d+$/;

export const isMobileAppBrowser = (): boolean => {
  // handle server-side rendering case
  if (typeof navigator === 'undefined') {
    return false;
  }
  const userAgent = navigator.userAgent ?? navigator.vendor ?? window?.['opera'];
  const isTablet = !!(/Linux/i.test(userAgent) && navigator?.maxTouchPoints > 1);
  const isIpad = !!(/Macintosh/i.test(userAgent) && navigator?.maxTouchPoints < 6 && navigator?.maxTouchPoints > 0);
  return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) || isTablet || isIpad;
};

export const generateApolloClientHeaders = (
  isLocalDev: boolean,
  pageContext?: any,
  extraHeaders: { [key: string]: string | boolean | ((...arg: string[]) => void) } = {}
) => {
  const currentLocale = pageContext?.currentLocale ?? 'en_US';
  const accessToken = pageContext?.sessionData?.cacheData?.data?.accessToken;
  const requestId = pageContext?.requestId || '';

  const context: any = {
    headers: {
      'accept-language': currentLocale?.replace('_', '-'),
      'x-request-id': requestId,
      ...extraHeaders,
    },
  };

  if (isLocalDev && accessToken) {
    context.headers = {
      ...context.headers,
      Authorization: `Bearer ${accessToken}`,
    };
  }

  return context;
};

export const submitDataFlow = async (formDataToSend: any, submitURL: string, marshaCode: string, requestId = '') => {
  // Make the API call
  const response = await fetch(`${submitURL}?marsha_cd=${marshaCode}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-request-id': requestId,
    },
    body: JSON.stringify(formDataToSend),
  });

  return response;
};

type SetPublicKeyType = (key: string) => void;
export const updatePublicKey = async (setPublicKey: SetPublicKeyType, requestId = '') => {
  try {
    const response = await axios.get(ENCRYPT_KEY_ENDPOINT, {
      headers: {
        'x-request-id': requestId,
      },
    });
    setPublicKey(response?.data?.rsaPublicKey);
    return;
  } catch (error) {
    // log.error('Error fetching data:', error);
  }
};

const { NEXT_PUBLIC_UPDATE_SESSION_URL_V2, NEXT_PUBLIC_PREFIX, PROXY_HOST } = getProcessEnvs();
export const isDev = process.env['NODE_ENV'];

let updateSessionUrl = `${NEXT_PUBLIC_PREFIX}${NEXT_PUBLIC_UPDATE_SESSION_URL_V2}`;
if (isDev) {
  updateSessionUrl = `${PROXY_HOST}${NEXT_PUBLIC_UPDATE_SESSION_URL_V2}`;
}

// Remove keys from Session Storage
export const removeFromSession = (sessionItems: any) => {
  for (let i = 0; i < sessionItems.length; i++) {
    sessionStorage.removeItem(sessionItems[i]);
  }
};
/* eslint-disable no-async-promise-executor */
export async function updateSession(data: Record<string, unknown>) {
  return new Promise(async (resolve, reject) => {
    if (process.env['IS_LOCAL_DEV'] === 'true') {
      resolve({ sessionHeader: '', sessionData: '' });
    } else {
      try {
        const sessionToken = Cookies.get('sessionID')?.toString() ?? '';
        const requestId = (data?.['requestId'] as string) || '';
        const payload = {
          ...data,
          sessionToken: (data?.sessionToken as string) || sessionToken,
        };
        const response = await fetch(`${updateSessionUrl}`, {
          method: 'PATCH',
          headers: {
            Cookie: sessionToken,
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'application/json',
            'x-request-id': requestId,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response?.json();
        resolve({
          sessionHeader: response.headers,
          sessionData: result,
        });
      } catch (error) {
        reject('Update session error');
      }
    }
  });
}
export const sanitizeStrictInput = (value: string = ''): string => {
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS: [],
    FORBID_TAGS: ['script'],
    ALLOWED_ATTR: [],
  });
};

export const removeXSSScripts = (value: string = ''): string => {
  if (!value) return '';
  // if (/[<>]/.test(value)) {
  //   return '';
  // }
  return sanitizeStrictInput(value);
};

export const getOptimizedImageUrl = (url: string): string => {
  if (!url) return '';

  // Check if the URL contains the specific path
  if (url.includes('/is/image')) {
    // Check if the URL already has query parameters to handle the '?' or '&' correctly
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}wid=400&hei=250&fit=crop`;
  }

  return url;
};

/**
 * Create a function to collect all mboxes from model.json
 */
export const collectMboxes = (node: AnyARecord, result: Set<string> = new Set()): string[] => {
  if (node && typeof node === 'object') {
    const nodeRecord = node as Record<string, any>;
    const mboxCandidates = [nodeRecord['mboxName'], nodeRecord['mboxParameter'], nodeRecord['targetMbox']];

    mboxCandidates.forEach(candidate => {
      if (typeof candidate === 'string' && candidate) {
        result.add(candidate);
      }
    });

    for (const key in nodeRecord) {
      if (typeof nodeRecord[key] === 'object') {
        collectMboxes(nodeRecord[key], result);
      }
    }
  }

  return [...result];
};

export const findSectionByMbox = (node: any, mboxName: string): any => {
  if (node?.mboxName === mboxName || node?.mboxParameter === mboxName || node?.targetMbox === mboxName) return node;

  if (node && typeof node === 'object') {
    for (const key in node) {
      const found = findSectionByMbox(node[key], mboxName);
      if (found) return found;
    }
  }
  return null;
};
